/**
 * WHAT EACH STORE ACTUALLY COSTS — the row set behind Locations & Billing.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS
 * ---------------------------------------------------------------------------
 * The card used to be fed by `location-requests` alone, which is the money
 * model that PRECEDED per-location billing: a location beyond the free quota
 * was approved at a price, and `isFree` on that row was the answer.
 *
 * A migrated tenant has none of that. Every store carries its OWN Stripe
 * subscription, and the request rows are frozen at whatever was decided the day
 * the location was approved — so a three-store account showed "Free" three
 * times while Stripe billed all three, and a fourth store that had a live
 * subscription but no request row did not appear on the money view AT ALL.
 *
 * So the rows are built from the LOCATIONS Stripe is billing, with the request
 * rows folded in only for what they are still authoritative about: a store
 * waiting to be approved.
 */

export interface DriftLocationLike {
  locationId: string;
  name?: string;
  subscriptionId?: string;
  nextInvoiceCents?: number;
  nextInvoiceAt?: number;
  nextInvoiceUnavailable?: string;
  stripeUnavailable?: string;
}

export interface LocationRequestLike {
  requestId: string;
  name?: string;
  status?: string;
  locationId?: string;
  isFree?: boolean;
  priceAmount?: number | null;
}

export interface LocationBillingRow {
  /** Stable react key: the locationId where there is one, else the requestId. */
  key: string;
  name: string;
  /** Known only where a request row says so; a drift-only store has none to state. */
  status?: string;
  /** Awaiting a decision — these keep the approval flow, which is unchanged. */
  pending: boolean;
  requestId?: string;
  locationId?: string;
  subscriptionId?: string;
  /** What Stripe will take next. Absent is NOT zero — see `unavailable`. */
  nextInvoiceCents?: number;
  nextInvoiceAt?: number;
  /** Why there is no figure. Rendered instead of an amount, never as "Free". */
  unavailable?: string;
}

const PENDING_STATUSES = new Set(['pending', 'info_requested']);

export const isPendingStatus = (status?: string): boolean =>
  PENDING_STATUSES.has(String(status || ''));

/**
 * TRUE when this tenant is billed per location.
 *
 * The honest signal is a location carrying a subscription of its own — the same
 * rule billing-service's `resolveBillingMode` applies. `tenant.stripeSubscriptionId`
 * proves nothing: every tenant who ever signed up through Stripe Checkout keeps
 * that field for ever.
 */
export function isPerLocationBilling(driftLocations: DriftLocationLike[] | null): boolean {
  return (driftLocations || []).some((l) => !!l.subscriptionId);
}

/**
 * One row per store, from the billing side, with pending requests folded in.
 *
 * A request is matched to a location by `locationId`. An approved request whose
 * location is already in the billing side adds nothing but its status; a
 * PENDING one has no location to bill yet and rides on its own row.
 */
export function buildLocationRows(args: {
  /** `null` — the billing read failed or has not run. Falls back to requests alone. */
  driftLocations: DriftLocationLike[] | null;
  requests: LocationRequestLike[];
}): LocationBillingRow[] {
  const requests = args.requests || [];
  const requestByLocationId = new Map<string, LocationRequestLike>();
  for (const r of requests) {
    if (r.locationId) requestByLocationId.set(r.locationId, r);
  }

  const rows: LocationBillingRow[] = [];
  const claimedRequestIds = new Set<string>();

  for (const loc of args.driftLocations || []) {
    const req = requestByLocationId.get(loc.locationId);
    if (req) claimedRequestIds.add(req.requestId);
    rows.push({
      key: loc.locationId,
      name: loc.name || req?.name || loc.locationId,
      ...(req?.status ? { status: req.status } : {}),
      pending: isPendingStatus(req?.status),
      ...(req?.requestId ? { requestId: req.requestId } : {}),
      locationId: loc.locationId,
      ...(loc.subscriptionId ? { subscriptionId: loc.subscriptionId } : {}),
      ...(typeof loc.nextInvoiceCents === 'number'
        ? { nextInvoiceCents: loc.nextInvoiceCents }
        : {}),
      ...(loc.nextInvoiceAt ? { nextInvoiceAt: loc.nextInvoiceAt } : {}),
      ...(typeof loc.nextInvoiceCents !== 'number'
        ? { unavailable: loc.nextInvoiceUnavailable || loc.stripeUnavailable || 'no billing figure could be read' }
        : {}),
    });
  }

  // Requests with no location on the billing side: stores still awaiting
  // approval, and — when the billing read failed entirely — every store.
  for (const req of requests) {
    if (claimedRequestIds.has(req.requestId)) continue;
    if (req.locationId && rows.some((r) => r.locationId === req.locationId)) continue;
    rows.push({
      key: req.requestId,
      name: req.name || req.requestId,
      ...(req.status ? { status: req.status } : {}),
      pending: isPendingStatus(req.status),
      requestId: req.requestId,
      ...(req.locationId ? { locationId: req.locationId } : {}),
      unavailable: isPendingStatus(req.status)
        ? 'not billed until approved'
        : 'no subscription on the billing side',
    });
  }

  // Pending first — they are the ones asking for a decision — then by name.
  return rows.sort((a, b) =>
    Number(b.pending) - Number(a.pending) || a.name.localeCompare(b.name));
}

/** What the whole estate is next invoiced, summed only over rows that HAVE a figure. */
export function totalNextInvoiceCents(rows: LocationBillingRow[]): number {
  return (rows || []).reduce(
    (sum, r) => sum + (typeof r.nextInvoiceCents === 'number' ? r.nextInvoiceCents : 0),
    0,
  );
}
